import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  take,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IAPIListResult,
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { ICreateMaterialGroup } from "./interfaces/create-material-group.interface";
import { IUpdateMaterialGroup } from "./interfaces/update-material-group.interface";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import MaterialGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/materials/material-group/material-group.entity";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_MATERIAL_URL;
const moduleName = "material_group";

@Injectable({
  providedIn: "root",
})
export class MaterialGroupService extends AbstractService<
  MaterialGroupAttributes,
  ICreateMaterialGroup,
  IUpdateMaterialGroup
> {
  /** The last filter used to load the list */
  currentFilter: CombinedFilter<MaterialGroupAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(
    filter: CombinedFilter<MaterialGroupAttributes>,
    options?: RequestOptions
  ) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.get<IPaginationResult<MaterialGroupAttributes>>(API_URL, {
      headers,
    });
  }

  // findToComboBoxWithoutPage(): Observable<
  //   IRequestResult<MaterialGroupAttributes>
  // > {
  //   const query = buildQuery<MaterialGroupAttributes>(
  //     orderBy("name", OrderVariant.Asc)
  //   );

  //   const API_URL: string =
  //     apiUrl +
  //     `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
  //   return this.http.get<IRequestResult<MaterialGroupAttributes>>(API_URL);
  // }

  /**
   * Obtiene grupos de materiales con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Tres objetos (OR lógico) - busca en 'code', 'name', 'externalKeys.code'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param companyIds Company filter
   * @param fatherCode Father code filter
   * @param dataFilter Additional filters
   * @returns Paginated data
   *
   * Search fields: code, name, externalKeys.code
   * Array filters: companyIds, divisionId, status
   * Single filters: materialGroupId, fatherCode
   */
  findAllWithCount(
    options: IPagination<MaterialGroupAttributes>,
    companyIds: number[],
    fatherCode?: string,
    dataFilter?: IFilters
  ): Observable<IPaginationResult<MaterialGroupAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtros de compañía, división, grupo, estados, fatherCode.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<MaterialGroupAttributes>[] => {
      const deletedFilter: FilterFn<MaterialGroupAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        companyIds && companyIds.length > 0
          ? andWhere('company.id' as keyof MaterialGroupAttributes, Ops.in(...companyIds))
          : noop(),
        dataFilter?.materialGroupId
          ? andWhere('code' as keyof MaterialGroupAttributes, Ops.eq(dataFilter.materialGroupId))
          : noop(),
        dataFilter?.status && dataFilter.status.length > 0
          ? andWhere('statusId', Ops.in(...dataFilter.status))
          : noop(),
        dataFilter?.divisionId && dataFilter.divisionId.length > 0
          ? andWhere('division.id' as keyof MaterialGroupAttributes, Ops.in(...dataFilter.divisionId))
          : noop(),
        fatherCode
          ? andWhere('code', Ops.eq(fatherCode))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<MaterialGroupAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop(),
      fatherCode ? orWhere("fatherCode", Ops.eq(fatherCode)) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<MaterialGroupAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i")),
      fatherCode ? orWhere("fatherCode", Ops.eq(fatherCode)) : noop()
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByExternalCodeQueryObject: FilterFn<MaterialGroupAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("externalKeys.code" as keyof MaterialGroupAttributes, Ops.like(options.search!, "i")),
      fatherCode ? orWhere("fatherCode", Ops.eq(fatherCode)) : noop()
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<MaterialGroupAttributes>(
      // Expandimos los tres objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,
      ...searchByExternalCodeQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "position",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialGroupAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(
    options: IPagination<MaterialGroupAttributes>
  ): Observable<IPaginationResult<MaterialGroupAttributes>> {
    const query = buildQuery<MaterialGroupAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"externalKeys.code", Ops.like(options.search ?? "", "i")),

      orderBy(
        options.sort ?? "name",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialGroupAttributes>>(API_URL);
  }

  comboBoxFather(
    hasSubcategories: boolean
  ): Observable<IAPIListResult<MaterialGroupAttributes>> {
    const query = buildQuery<MaterialGroupAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("statusId", StatusEnum.ACTIVE),
      andWhere("hasSubcategories", Ops.eq(hasSubcategories)),
      select(["_id", "code", "name", "hasSubcategories", "fatherCode"]),
      orderBy("name", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IAPIListResult<MaterialGroupAttributes>>(API_URL);
  }

  fetchCategories(
    companyId: number,
    divisionIds: number[],
    categoryCode: string
  ): Observable<IAPIListResult<MaterialGroupAttributes>> {
    const query = buildQuery<MaterialGroupAttributes>(
      where("_deleted", Ops.eq(false)),
      andWhere("statusId", StatusEnum.ACTIVE),
      andWhere(<any>"company.id", Ops.in(companyId)),
      divisionIds && divisionIds.length > 0
        ? andWhere(<any>"division.id", Ops.in(...divisionIds))
        : noop(),
      categoryCode == "0"
        ? noop()
        : orWhere(<any>"hierarchy.code", Ops.eq(categoryCode)),
      select(["_id", "code", "name", "hasSubcategories", "fatherCode"]),
      orderBy("name", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IAPIListResult<MaterialGroupAttributes>>(API_URL);
  }

  sortGlobal(ids) {
    const API_URL: string = apiUrl + `api/${moduleName}/sort`;
    return this.http.post<any>(API_URL, ids);
  }

  saveFileMaterialGroup(file: File, optionLoad: string) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("transform", optionLoad);
    const API_URL: string = apiUrl + `api/${moduleName}/uploadv2`;
    return this.http.post<IRequestResult<MaterialGroupAttributes[]>>(
      API_URL,
      formData
    );
  }

  getMaterialGroupExcelData() {
    return env.GROUP_MATERIAL_LOAD_DATA;
  }
}
